<template>
    <Modal
        v-model="visible"
        title="日志详细内容"
        width="850"
        footer-hide
        :styles="{top: '50px'}"
        class-name="ys-log-detail-modal">

        <div class="log-detail-content" v-if="currentLog">
            <!-- 头部概览 -->
            <div class="detail-header">
                <div class="header-main">
                    <Tag :color="getBusinessTypeColor(currentLog.businessType)" size="large">
                        {{ getBusinessTypeText(currentLog.businessType) }}
                    </Tag>
                    <span class="business-code">{{ currentLog.businessCode || '无业务编号' }}</span>
                </div>
                <div class="header-sub">
                    <Icon type="md-clock" /> {{ formatTimestamp(currentLog.timestamp) }}
                </div>
            </div>

            <!-- 数据网格 -->
            <div class="info-grid">
                <section class="info-section">
                    <div class="section-title"><Icon type="md-contact" /> 用户与客户</div>
                    <div class="info-item-list">
                        <div class="info-item">
                            <span class="label">操作用户</span>
                            <span class="value">{{ currentLog.userName || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">客户姓名</span>
                            <span class="value">{{ currentLog.customerName || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">联系电话</span>
                            <span class="value">{{ currentLog.customerPhone || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">成交金额</span>
                            <span class="value amount">
                                {{ currentLog.totalMoneyActual ? '¥ ' + currentLog.totalMoneyActual.toLocaleString('zh-CN', {minimumFractionDigits: 2}) : '-' }}
                            </span>
                        </div>
                    </div>
                </section>

                <section class="info-section">
                    <div class="section-title"><Icon type="md-link" /> 系统关联</div>
                    <div class="info-item-list">
                        <div class="info-item">
                            <span class="label">用友订单号</span>
                            <span class="value copyable" @click="copyText(currentLog.ysOrderCode)">
                                {{ currentLog.ysOrderCode || '未同步' }} <Icon type="md-copy" v-if="currentLog.ysOrderCode" />
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="label">用友订单ID</span>
                            <span class="value copyable" @click="copyText(currentLog.ysOrderId)">
                                {{ currentLog.ysOrderId || '-' }} <Icon type="md-copy" v-if="currentLog.ysOrderId" />
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="label">业务UUID</span>
                            <span class="value uuid" :title="currentLog.businessUuid">{{ currentLog.businessUuid || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="label">苗场UUID</span>
                            <span class="value uuid" :title="currentLog.seedFarmUuid">{{ currentLog.seedFarmUuid || '-' }}</span>
                        </div>
                    </div>
                </section>
            </div>

            <!-- 错误信息 -->
            <div class="error-panel" v-if="currentLog.errorMessage || currentLog.errorType">
                <div class="error-header">
                    <Icon type="md-alert" />
                    <span class="error-title">异常详情 - {{ currentLog.errorType || 'Unknown Error' }}</span>
                </div>
                <div class="error-body">
                    <div class="error-msg">{{ currentLog.errorMessage }}</div>
                    <div class="stack-trace-wrapper" v-if="currentLog.errorStackTrace">
                        <div class="stack-header">
                            <span>Stack Trace</span>
                            <Button size="small" type="text" ghost @click="copyText(currentLog.errorStackTrace)">复制堆栈</Button>
                        </div>
                        <pre class="stack-content">{{ currentLog.errorStackTrace }}</pre>
                    </div>
                </div>
            </div>

            <!-- 备注信息 -->
            <div class="remarks-panel" v-if="currentLog.remarks">
                <div class="panel-title">备注信息</div>
                <div class="remarks-content">{{ currentLog.remarks }}</div>
            </div>
        </div>

        <div v-else class="empty-state">
            <Spin size="large"></Spin>
            <p>正在加载详情...</p>
        </div>
    </Modal>
</template>

<script>
export default {
    name: "YSLogDetailModal",
    data() {
        return {
            visible: false,
            currentLog: null
        };
    },
    methods: {
        open(logData) {
            this.currentLog = logData;
            this.visible = true;
        },
        handleCancel() {
            this.visible = false;
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return '-';
            return new Date(timestamp).toLocaleString('zh-CN', { hour12: false });
        },
        getBusinessTypeText(type) {
            const typeMap = {
                'VoucherOrder': '销售订单',
                'Other': '其他'
            };
            return typeMap[type] || type || '未知类型';
        },
        getBusinessTypeColor(type) {
            const colorMap = {
                'VoucherOrder': 'blue',
                'Other': 'default'
            };
            return colorMap[type] || 'default';
        },
        copyText(text) {
            if (!text) return;
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$Message.success('已复制到剪贴板');
                });
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                this.$Message.success('已复制到剪贴板');
            }
        }
    }
};
</script>

<style lang="less" scoped>
.log-detail-content {
    padding: 8px 16px 24px;

    .detail-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        padding-bottom: 20px;
        margin-bottom: 24px;
        border-bottom: 1px dashed #e8eaec;

        .header-main {
            display: flex;
            align-items: center;
            gap: 12px;
            .business-code {
                font-size: 20px;
                font-weight: 600;
                color: #17233d;
                font-family: 'Consolas', monospace;
            }
        }
        .header-sub {
            color: #808695;
            font-size: 13px;
        }
    }

    .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        margin-bottom: 24px;

        .info-section {
            .section-title {
                font-size: 15px;
                font-weight: 600;
                color: #1c2438;
                margin-bottom: 16px;
                display: flex;
                align-items: center;
                gap: 8px;
                
                i { color: #2d8cf0; }
            }

            .info-item-list {
                background: #f8f9fa;
                padding: 16px;
                border-radius: 8px;

                .info-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px solid rgba(0,0,0,0.03);
                    
                    &:last-child { border-bottom: none; }

                    .label { color: #808695; font-size: 13px; }
                    .value {
                        color: #17233d;
                        font-weight: 500;
                        text-align: right;
                        word-break: break-all;
                        max-width: 200px;
                        
                        &.amount { color: #ed4014; font-weight: 700; font-family: monospace; }
                        &.copyable {
                            color: #2d8cf0;
                            cursor: pointer;
                            &:hover { text-decoration: underline; }
                        }
                        &.uuid {
                            font-size: 11px;
                            color: #808695;
                            font-family: monospace;
                        }
                    }
                }
            }
        }
    }

    .error-panel {
        background: #fff1f0;
        border: 1px solid #ffa39e;
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 24px;

        .error-header {
            background: #ffccc7;
            padding: 10px 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            color: #cf1322;
            
            .error-title { font-weight: 600; }
        }

        .error-body {
            padding: 16px;
            
            .error-msg {
                font-size: 14px;
                color: #cf1322;
                font-weight: 500;
                margin-bottom: 12px;
                line-height: 1.6;
            }

            .stack-trace-wrapper {
                background: #1e1e1e;
                border-radius: 4px;
                
                .stack-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 4px 12px;
                    border-bottom: 1px solid #333;
                    color: #888;
                    font-size: 11px;
                }

                .stack-content {
                    margin: 0;
                    padding: 12px;
                    color: #d4d4d4;
                    font-size: 12px;
                    font-family: 'Consolas', 'Courier New', monospace;
                    max-height: 250px;
                    overflow-y: auto;
                    white-space: pre-wrap;
                    
                    &::-webkit-scrollbar { width: 6px; }
                    &::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }
                }
            }
        }
    }

    .remarks-panel {
        .panel-title {
            font-size: 15px;
            font-weight: 600;
            margin-bottom: 12px;
            color: #17233d;
        }
        .remarks-content {
            background: #fffbe6;
            border: 1px solid #ffe58f;
            padding: 12px 16px;
            border-radius: 6px;
            color: #856404;
        }
    }
}

.empty-state {
    text-align: center;
    padding: 60px 0;
    color: #808695;
    p { margin-top: 16px; }
}
</style>
